import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MetricChart, type MetricChartSpec, useMetricChart } from '@/components/charts/generic';
import { ChartKey } from '@/components/charts/legend';
import { useChartTheme } from '@/components/charts/theme';
import ContentBlock from '@/components/molecules/ContentBlock';
import { OptoColors } from '@/config/settings';
import type { DailyWeightTimeSeries } from '@/domain/smoltReport';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { parseLocale } from '@/utils/localeFormatters';

import {
    buildSmoltMeasurementsSpec,
    buildSmoltTimeSeriesSpec,
    hasLastFeedLine,
    LAST_FEED_LINE_COLOR,
    LIVING_WEIGHT_AXIS,
    smoltAxis,
} from '../utils/smoltChartSpecs';

import styles from './SmoltWeightTimeSeriesCharts.module.scss';

/** Chart.js' own default proportions, which these charts always had. */
const ASPECT_RATIO = 2;

interface ChartLineLegendItem {
    color: string;
    label: string;
    dashed?: boolean;
}

interface ChartLineLegendProps {
    items: ChartLineLegendItem[];
}

function ChartLineLegend(props: ChartLineLegendProps) {
    const { items } = props;
    return (
        <ChartKey
            className={styles.chartLineLegend}
            entries={items.map((item) => ({
                id: item.label,
                label: item.label,
                swatch: { shape: item.dashed ? 'dashed' : 'line', color: item.color },
            }))}
        />
    );
}

const OCEAN_GREEN_COLOR = OptoColors.OceanGreen;
const RIVERSIDE_BLUE_COLOR = OptoColors.Riverside;

interface SmoltTimeSeriesChartProps<TKind extends 'line' | 'bar'> {
    spec: MetricChartSpec<TKind>;
    isLoading: boolean;
}

function SmoltTimeSeriesChart<TKind extends 'line' | 'bar'>(
    props: SmoltTimeSeriesChartProps<TKind>
) {
    const { spec, isLoading } = props;
    const chart = useMetricChart(spec);
    return (
        <MetricChart
            kind={spec.kind}
            data={chart.data}
            options={chart.options}
            legendItems={chart.legendItems}
            visibleSeries={chart.visibleSeries}
            onToggleSeries={chart.toggleSeries}
            chartRef={chart.chartRef}
            aspectRatio={ASPECT_RATIO}
            isLoading={isLoading}
        />
    );
}

interface SmoltWeightTimeSeriesChartsProps {
    timeSeries: DailyWeightTimeSeries[];
    lastFeedingDate?: string | null;
    isLoading?: boolean;
    isPdfExport?: boolean;
}

export default function SmoltWeightTimeSeriesCharts(props: SmoltWeightTimeSeriesChartsProps) {
    const { timeSeries, lastFeedingDate, isLoading = false, isPdfExport = false } = props;

    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);
    // The header legends are HTML, so they take the colours the charts draw in by hand.
    const chartTheme = useChartTheme();

    const lastFeedLegendItem: ChartLineLegendItem | null = hasLastFeedLine(
        timeSeries,
        lastFeedingDate
    )
        ? {
              color: chartTheme.color(LAST_FEED_LINE_COLOR),
              label: t('smoltReport.detailView.headerLastFeedingDate'),
              dashed: true,
          }
        : null;

    const specs = useMemo(() => {
        const common = { timeSeries, locale, lastFeedingDate };
        return {
            livingWeight: buildSmoltTimeSeriesSpec({
                ...common,
                lines: [
                    {
                        key: 'liveWeight',
                        label: t('smoltReport.detailView.livingWeightLabel'),
                        color: OCEAN_GREEN_COLOR,
                        style: 'points',
                        value: (day) => day.liveWeight,
                    },
                    {
                        key: 'liveWeightTrend',
                        label: t('smoltReport.detailView.livingWeightTrendLabel'),
                        color: RIVERSIDE_BLUE_COLOR,
                        style: 'trend',
                        value: (day) => day.liveWeightTrend,
                    },
                ],
                left: LIVING_WEIGHT_AXIS,
            }),
            sgr: buildSmoltTimeSeriesSpec({
                ...common,
                lines: [
                    {
                        key: 'sgr',
                        label: t('smoltReport.detailView.sgrLabel'),
                        color: OCEAN_GREEN_COLOR,
                        style: 'line',
                        value: (day) => day.sgr,
                    },
                ],
                left: smoltAxis('%', 2, 2),
            }),
            size: buildSmoltTimeSeriesSpec({
                ...common,
                lines: [
                    {
                        key: 'fishHeight',
                        label: t('smoltReport.detailView.fishHeightLabel'),
                        color: RIVERSIDE_BLUE_COLOR,
                        style: 'line',
                        value: (day) => day.fishHeight,
                    },
                    {
                        key: 'fishLength',
                        label: t('smoltReport.detailView.fishLengthLabel'),
                        color: OCEAN_GREEN_COLOR,
                        style: 'line',
                        value: (day) => day.fishLength,
                        axis: 'right',
                    },
                ],
                left: smoltAxis('cm', 1, 2),
                right: smoltAxis('cm', 1, 2),
            }),
            growth: buildSmoltTimeSeriesSpec({
                ...common,
                lines: [
                    {
                        key: 'growth',
                        label: t('smoltReport.detailView.growthLabel'),
                        color: OCEAN_GREEN_COLOR,
                        style: 'line',
                        value: (day) => day.growth,
                    },
                ],
                left: smoltAxis('g', 1, 1),
            }),
            cv: buildSmoltTimeSeriesSpec({
                ...common,
                lines: [
                    {
                        key: 'cv',
                        label: t('smoltReport.detailView.cvLabel'),
                        color: RIVERSIDE_BLUE_COLOR,
                        style: 'points',
                        value: (day) => day.cv,
                    },
                ],
                left: smoltAxis('%', 1, 1),
            }),
            measurements: buildSmoltMeasurementsSpec({
                ...common,
                label: t('smoltReport.detailView.dailyMeasurementsLabel'),
                color: OCEAN_GREEN_COLOR,
            }),
        };
    }, [timeSeries, locale, lastFeedingDate, t]);

    const showMeasurements = timeSeries.some((day) => day.numMeasurements != null);

    if (!timeSeries?.length) {
        return <></>;
    }

    return (
        <GridLayout
            keepCellsTogetherInPdf
            contentLayout="2"
            responsive={!isPdfExport}
            className={styles.chartsGrid}>
            <ContentBlock
                title={t('smoltReport.detailView.livingWeight')}
                spacing="none"
                headerActions={
                    lastFeedLegendItem && <ChartLineLegend items={[lastFeedLegendItem]} />
                }>
                <SmoltTimeSeriesChart spec={specs.livingWeight} isLoading={isLoading} />
            </ContentBlock>
            <ContentBlock
                title={t('smoltReport.detailView.sgr')}
                spacing="none"
                headerActions={
                    lastFeedLegendItem && <ChartLineLegend items={[lastFeedLegendItem]} />
                }>
                <SmoltTimeSeriesChart spec={specs.sgr} isLoading={isLoading} />
            </ContentBlock>
            <ContentBlock
                title={t('smoltReport.detailView.size')}
                spacing="none"
                headerActions={
                    <ChartLineLegend
                        items={[
                            {
                                color: chartTheme.color(RIVERSIDE_BLUE_COLOR),
                                label: t('smoltReport.detailView.fishHeightLabel'),
                            },
                            {
                                color: chartTheme.color(OCEAN_GREEN_COLOR),
                                label: t('smoltReport.detailView.fishLengthLabel'),
                            },
                            ...(lastFeedLegendItem ? [lastFeedLegendItem] : []),
                        ]}
                    />
                }>
                <SmoltTimeSeriesChart spec={specs.size} isLoading={isLoading} />
            </ContentBlock>
            <ContentBlock
                title={t('smoltReport.detailView.growth')}
                spacing="none"
                headerActions={
                    lastFeedLegendItem && <ChartLineLegend items={[lastFeedLegendItem]} />
                }>
                <SmoltTimeSeriesChart spec={specs.growth} isLoading={isLoading} />
            </ContentBlock>
            <ContentBlock
                title={t('smoltReport.detailView.cv')}
                spacing="bottom"
                headerActions={
                    lastFeedLegendItem && <ChartLineLegend items={[lastFeedLegendItem]} />
                }>
                <SmoltTimeSeriesChart spec={specs.cv} isLoading={isLoading} />
            </ContentBlock>
            {showMeasurements && (
                <ContentBlock
                    title={t('smoltReport.detailView.dailyMeasurements')}
                    spacing="bottom"
                    headerActions={
                        lastFeedLegendItem && <ChartLineLegend items={[lastFeedLegendItem]} />
                    }>
                    <SmoltTimeSeriesChart spec={specs.measurements} isLoading={isLoading} />
                </ContentBlock>
            )}
        </GridLayout>
    );
}
